import React from 'react';
import createTableStore from '@store/table-store/table.store.ts';
import { ApiResponse } from '@models/api.model.ts';

export interface ApiFunction<T> {
  (): Promise<T>;
}

export interface RenderFilterItemProps {
  option: any;
  isSelected: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface Column {
  id: string;
  label: any;
  field: string;
  render: (value: any, row: any) => React.ReactNode;
  renderFilterItem?: (props: RenderFilterItemProps) => React.ReactNode;
  showSort?: boolean;
  showSearch?: boolean;
  showFilter?: boolean;
  showSearchInput?: boolean;
  selectionMode?: SelectionMode;
  filterData?: any;
  isTranslated?: boolean;
  showDateRangeFilter?: boolean;
  width?: number;
  disabledDrag?: boolean;
}

export interface TableSearchIconProps {
  columnId: string;
  value: string;
  onSearchChange: (columnId: string, value: string) => void;
  clearSearch?: (columnId: string) => void;
}

export interface TableColumnFilterProps {
  columnId: string;
  value: string[];
  filterData?: any;
  showSearchInput?: boolean;
  selectionMode?: SelectionMode;
  onFilterChange?: (columnId: string, value: string[]) => void;
  clearFilters?: (columnId: string) => void;
  renderFilterItem?: (props: RenderFilterItemProps) => React.ReactNode;
}

export interface DynamicTableProps<T> {
  columns: Column[];
  apiFunction: ApiFunction<T>;
  tableStore: ReturnType<typeof createTableStore>;
  language?: string;
  onTriggerAction?: (type: TriggerTypeEnum) => void;
  setupForMe?: boolean;
  setupLoading?: boolean;
  initialState?: {
    sort?: [string, SortOrder];
    search?: Record<string, string>;
    filters?: Record<string, string[]>;
    between?: Record<string, { from: string; to: string }>;
    page?: number;
    limit?: number;
  };
  tableKey?: string;
  onRowDoubleClick?: (row: any) => void;
  hideEmptyButton?: boolean;
  extraFilterButtons?: React.ReactNode;
  hiddenFilters?: string[];
}

export type UseDynamicTableLogicParams<T extends ApiResponse<any>> = {
  columns: Column[];
  apiFunction: ApiFunction<T>;
  tableStore: DynamicTableProps<T>['tableStore'];
  language?: string;
  onTriggerAction?: (trigger: TriggerTypeEnum) => void;
  setupForMe?: boolean;
  setupLoading?: boolean;
  initialState?: DynamicTableProps<T>['initialState'];
  tableKey?: string;
};

export interface ColumnDef {
  field: string;
  id: string;
  label: string;
  isTranslated?: boolean;
  filterData?: any[];
}

export interface ClearAllFiltersProps {
  orderedColumns: ColumnDef[];
  search: Record<string, string>;
  filters: Record<string, string[]>;
  sort: [string, 'asc' | 'desc' | ''];
  betweenDateRangeFilters: Record<string, { from: string; to: string }>;
  clearSearch?: (columnId: string) => void;
  clearSort: () => void;
  clearFilters?: (columnId: string) => void;
  clearDateRangeFilters?: (columnId: string) => void;
  clearAllFilters: () => void;
  extraFilterButtons?: React.ReactNode;
  hiddenFilters?: string[];
}

export interface ActionProps<T> {
  row: T;
  handleEdit?: (row: T) => void;
  handleDelete?: (row: T) => void;
  handlePreview?: (row: T) => void;
  hideDelete?: boolean;
  hidePreview?: boolean;
}

export interface TableSelectionColumnParams<T> {
  handleSelectRow: (row: T) => void;
  handleSelectAll: () => void;
  isPageAllSelected: boolean;
  isPagePartiallySelected: boolean;
  isRowSelected: (row: T) => boolean;
  isRowSelectable?: (row: T) => boolean;
}

export enum TriggerTypeEnum {
  Setup = 'setup',
  Add = 'add'
}

export enum SelectionMode {
  Single = 'single',
  Multiple = 'multiple'
}

export enum SortOrder {
  Ascending = 'asc',
  Descending = 'desc'
}

export const statusOptionsForDeviceType = [
  { title: 'Tv', color: 'var(--teal)', value: 'tv' },
  { title: 'Tablet', color: 'var(--red-3)', value: 'tablet' }
];

export const statusOptionsForOs = [
  { title: 'Android', color: 'var(--teal)', value: 'android' },
  { title: 'Tizen', color: 'var(--yellow)', value: 'tizen' },
  { title: 'Webos', color: 'var(--yellow)', value: 'webos' },
  { title: 'FireTv', color: 'var(--red-3)', value: 'fireTv' },
  { title: 'Pos', color: 'var(--red-3)', value: 'pos' },
  { title: 'Roku', color: 'var(--red-3)', value: 'roku' }
];
